import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { CustomerLayout } from "@/components/Layout/CustomerLayout";
import api from "@/lib/api";
import { useLang } from "@/contexts/LanguageContext";
import { useCurrency } from "@/contexts/CurrencyContext";
import { Check, Bike, ChefHat, Receipt, MapPin, Star, ChevronLeft } from "lucide-react";

const STEPS = [
  { key: "pending", icon: Receipt, label: "Order placed" },
  { key: "confirmed", icon: Check, label: "Confirmed" },
  { key: "preparing", icon: ChefHat, label: "Preparing" },
  { key: "ready", icon: Check, label: "Ready for pickup" },
  { key: "picked_up", icon: Bike, label: "Rider picked up" },
  { key: "out_for_delivery", icon: Bike, label: "On the way" },
  { key: "delivered", icon: MapPin, label: "Delivered" },
];

export default function OrderTracking() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useLang();
  const { formatPrice } = useCurrency();
  const [order, setOrder] = useState(null);
  const [reviewing, setReviewing] = useState(false);
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    const load = () => api.get(`/orders/${id}`).then(r => setOrder(r.data));
    load();
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [id]);

  const submitReview = async () => {
    await api.post("/reviews", { restaurant_id: order.restaurant_id, order_id: order.id, rating, comment });
    setSubmitted(true); setReviewing(false);
  };

  if (!order) return <CustomerLayout><div className="p-8">Loading...</div></CustomerLayout>;

  const currentIdx = STEPS.findIndex(s => s.key === order.status);

  return (
    <CustomerLayout>
      <div className="max-w-2xl mx-auto px-4 py-6">
        <button onClick={() => navigate("/app/orders")} className="text-sm text-[var(--so-text-soft)] flex items-center gap-1 mb-4 hover:text-[var(--so-orange)]" data-testid="back-orders">
          <ChevronLeft className="w-4 h-4" /> {t("yourOrders")}
        </button>

        <div className="so-card p-5 flex gap-3 items-center">
          <img src={order.restaurant_image} alt={order.restaurant_name} className="w-16 h-16 rounded-xl object-cover" />
          <div className="flex-1">
            <div className="font-bold">{order.restaurant_name}</div>
            <div className="text-xs text-[var(--so-text-soft)]">Order #{order.id.slice(0, 8)}</div>
            <div className="text-xs text-[var(--so-text-soft)] mt-0.5">{new Date(order.created_at).toLocaleString()}</div>
          </div>
          <div className="text-end">
            <div className="font-bold text-lg text-[var(--so-orange)]" data-testid="order-total">{formatPrice(order.total)}</div>
            <div className="text-xs text-[var(--so-text-soft)]">{order.payment_method}</div>
          </div>
        </div>

        {/* Timeline */}
        <div className="mt-5 so-card p-5">
          <div className="text-xs font-bold uppercase tracking-wider text-[var(--so-text-soft)] mb-4">Live status</div>
          {STEPS.map((s, i) => {
            const done = i < currentIdx;
            const active = i === currentIdx;
            return (
              <div key={s.key} className={`so-timeline-step ${done ? "done" : ""} ${active ? "active" : ""}`} data-testid={`step-${s.key}`}>
                <div className="dot">{done && <Check className="w-3 h-3 text-white" />}</div>
                <div>
                  <div className={`font-semibold text-sm ${(done || active) ? "" : "text-[var(--so-text-soft)]"}`}>{s.label}</div>
                  {active && <div className="text-xs text-[var(--so-orange)] font-bold animate-pulse">In progress</div>}
                </div>
              </div>
            );
          })}
          {order.rider_name && <div className="mt-4 text-sm border-t border-[var(--so-border)] pt-3"><b>Rider:</b> {order.rider_name}</div>}
        </div>

        {/* Items */}
        <div className="mt-5 so-card p-5">
          <div className="font-bold mb-3">Items</div>
          {order.items.map((it) => (
            <div key={it.menu_item_id} className="flex justify-between text-sm py-1.5 border-b border-[var(--so-border)] last:border-0">
              <span>{it.quantity}× {it.name}</span>
              <span className="font-semibold">{formatPrice(it.line_total)}</span>
            </div>
          ))}
        </div>

        {/* Review */}
        {order.status === "delivered" && !submitted && !reviewing && (
          <button onClick={() => setReviewing(true)} className="so-btn-secondary w-full justify-center mt-5" data-testid="open-review">
            <Star className="w-4 h-4" /> {t("leaveReview")}
          </button>
        )}
        {reviewing && (
          <div className="mt-5 so-card p-5">
            <div className="font-bold mb-2">{t("leaveReview")}</div>
            <div className="flex gap-1 mb-3">
              {[1,2,3,4,5].map(n => (
                <button key={n} onClick={() => setRating(n)} data-testid={`star-${n}`}>
                  <Star className={`w-7 h-7 ${n <= rating ? "fill-amber-400 text-amber-400" : "text-gray-300"}`} />
                </button>
              ))}
            </div>
            <textarea className="so-input" rows={3} placeholder="Tell us how it went..." value={comment} onChange={e => setComment(e.target.value)} data-testid="review-comment" />
            <button onClick={submitReview} className="so-btn-primary w-full justify-center mt-3" data-testid="submit-review">{t("submitReview")}</button>
          </div>
        )}
        {submitted && <div className="mt-5 so-card p-5 text-center text-emerald-600 font-bold">Thanks for your review! ⭐</div>}
      </div>
    </CustomerLayout>
  );
}
